import { getUIColorPalette } from "./ColorPalette";

export const getDelayStatusTextColor = ( isDarkUi: boolean, delaySec: number ): string => {
    const palette = getUIColorPalette( isDarkUi );
    if ( delaySec > 0 ) {
        return palette.statusDelayText;
    }
    if ( delaySec < 0 ) {
        return palette.statusAdvanceText;
    }

    return palette.overlayTextSecondary;
};

/** Background, text, and border colors for the HH:MM pill inside map stop labels. */
export interface MapStopArrivalTimePillPresentation {
    "backgroundColor": string;
    "textColor":       string;
    "borderColor":     string;
}

/** Pale green background for on-time arrival pills. */
const MAP_STOP_ARRIVAL_PILL_ON_TIME_BACKGROUND = "#BBF7D0";

/** Text color on on-time arrival pills. */
const MAP_STOP_ARRIVAL_PILL_ON_TIME_TEXT = "#14532D";

/** Pale red background for early arrival pills in light UI. */
const MAP_STOP_ARRIVAL_PILL_ADVANCE_BACKGROUND_LIGHT = "#FECACA";

/** High-contrast text on pale red early pills in light UI. */
const MAP_STOP_ARRIVAL_PILL_ADVANCE_TEXT_LIGHT = "#991B1B";

/** Early pill background in dark UI (stronger fill for map legibility). */
const MAP_STOP_ARRIVAL_PILL_ADVANCE_BACKGROUND_DARK = "#B91C1C";

/** Text color on early pills in dark UI. */
const MAP_STOP_ARRIVAL_PILL_ADVANCE_TEXT_DARK = "#FEE2E2";

/** Pale violet background for late arrival pills in light UI. */
const MAP_STOP_ARRIVAL_PILL_LATE_BACKGROUND_LIGHT = "#DDD6FE";

/** High-contrast text on pale violet late pills in light UI. */
const MAP_STOP_ARRIVAL_PILL_LATE_TEXT_LIGHT = "#4C1D95";

/** Late pill background in dark UI (stronger fill for map legibility). */
const MAP_STOP_ARRIVAL_PILL_LATE_BACKGROUND_DARK = "#6D28D9";

/** Text color on late pills in dark UI. */
const MAP_STOP_ARRIVAL_PILL_LATE_TEXT_DARK = "#F5F3FF";

/** Dark gray border shared by all arrival-time pills. */
const MAP_STOP_ARRIVAL_PILL_BORDER = "rgba(51, 65, 85, 0.55)";

/** Opaque neutral pill background in light UI. */
const MAP_STOP_ARRIVAL_PILL_NEUTRAL_BACKGROUND_LIGHT = "#E2E8F0";

/** Opaque neutral pill background in dark UI. */
const MAP_STOP_ARRIVAL_PILL_NEUTRAL_BACKGROUND_DARK = "#334155";

/**
 * Resolves pill colors for the HH:MM badge in map stop label bubbles.
 *
 * - Late realtime arrivals: pale violet pill with high-contrast text.
 * - Early realtime arrivals: pale red pill aligned with {@link UIColorPalette.statusAdvanceText}.
 * - On-time realtime arrivals: green pill.
 * - Scheduled / no delay coloring: opaque neutral pill with theme-adapted text.
 *
 * @param isDarkUi Whether dark theme is currently active.
 * @param delaySec Signed delay in seconds (positive = late, negative = early).
 * @param hasRealtimeDelay Whether realtime delay data is available for this stop.
 * @param applyDelayColoring Whether delay-based coloring is enabled via config toggles.
 * @returns Background and text colors for the arrival-time pill.
 */
export const getMapStopArrivalTimePillPresentation = (
    isDarkUi: boolean,
    delaySec: number,
    hasRealtimeDelay: boolean,
    applyDelayColoring: boolean,
): MapStopArrivalTimePillPresentation => {
    const palette = getUIColorPalette( isDarkUi );
    if ( applyDelayColoring && hasRealtimeDelay ) {
        if ( delaySec > 0 ) {
            return {
                "backgroundColor": isDarkUi
                    ? MAP_STOP_ARRIVAL_PILL_LATE_BACKGROUND_DARK
                    : MAP_STOP_ARRIVAL_PILL_LATE_BACKGROUND_LIGHT,
                "textColor": isDarkUi
                    ? MAP_STOP_ARRIVAL_PILL_LATE_TEXT_DARK
                    : MAP_STOP_ARRIVAL_PILL_LATE_TEXT_LIGHT,
                "borderColor": MAP_STOP_ARRIVAL_PILL_BORDER,
            };
        }

        if ( delaySec < 0 ) {
            return {
                "backgroundColor": isDarkUi
                    ? MAP_STOP_ARRIVAL_PILL_ADVANCE_BACKGROUND_DARK
                    : MAP_STOP_ARRIVAL_PILL_ADVANCE_BACKGROUND_LIGHT,
                "textColor": isDarkUi
                    ? MAP_STOP_ARRIVAL_PILL_ADVANCE_TEXT_DARK
                    : MAP_STOP_ARRIVAL_PILL_ADVANCE_TEXT_LIGHT,
                "borderColor": MAP_STOP_ARRIVAL_PILL_BORDER,
            };
        }

        return {
            "backgroundColor": MAP_STOP_ARRIVAL_PILL_ON_TIME_BACKGROUND,
            "textColor":       MAP_STOP_ARRIVAL_PILL_ON_TIME_TEXT,
            "borderColor":     MAP_STOP_ARRIVAL_PILL_BORDER,
        };
    }

    return {
        "backgroundColor": isDarkUi
            ? MAP_STOP_ARRIVAL_PILL_NEUTRAL_BACKGROUND_DARK
            : MAP_STOP_ARRIVAL_PILL_NEUTRAL_BACKGROUND_LIGHT,
        "textColor":   palette.overlayTextPrimary,
        "borderColor": MAP_STOP_ARRIVAL_PILL_BORDER,
    };
};
